import React, { useState } from 'react';
import { MessageSquare, Send, CheckCircle, XCircle, BarChart, Users } from 'lucide-react';

export default function SlackTester() {
  const [command, setCommand] = useState('/approve');
  const [text, setText] = useState('Should we deploy to production?');
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const commands = [
    {
      cmd: '/approve',
      description: 'Simple yes/no approval',
      example: 'Should we deploy to production?',
      icon: <CheckCircle className="text-green-500" size={20} />
    },
    {
      cmd: '/poll',
      description: 'Multi-option poll',
      example: 'What for lunch? Pizza | Tacos | Sushi',
      icon: <BarChart className="text-blue-500" size={20} />
    },
    {
      cmd: '/decide',
      description: 'Contextual decision',
      example: 'Performance issue | Fix now or after launch? | Fix now | Ship first',
      icon: <Users className="text-purple-500" size={20} />
    }
  ];

  const simulateSlackCommand = async () => {
    setLoading(true);
    setResponse(null);

    try {
      // Simulate Slack command payload
      const payload = new URLSearchParams({
        command: command,
        text: text,
        user_name: 'test_user',
        user_id: 'U123456',
        channel_id: 'C123456',
        team_id: 'T123456',
        response_url: 'https://hooks.slack.com/commands/1234/5678'
      });

      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/slack/events'
        : 'https://futr-bet-api.b-9f2.workers.dev/slack/events';

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: payload.toString()
      });

      const data = await res.json();
      setResponse(data);
    } catch (error) {
      setResponse({
        error: true,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  const simulateInteraction = async (callbackId: string, value: string) => {
    setLoading(true);

    try {
      const interactionPayload = {
        type: 'interactive_message',
        callback_id: callbackId,
        user: {
          id: 'U123456',
          name: 'test_user'
        },
        actions: [{
          name: 'response',
          value: value,
          type: 'button'
        }],
        response_url: 'https://hooks.slack.com/commands/1234/5678',
        original_message: response
      };

      const payload = new URLSearchParams({
        payload: JSON.stringify(interactionPayload)
      });

      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/slack/interactive'
        : 'https://futr-bet-api.b-9f2.workers.dev/slack/interactive';

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: payload.toString()
      });

      const data = await res.json();
      setResponse(data);
    } catch (error) {
      setResponse({
        error: true,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Animated Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="relative">
              <MessageSquare size={50} className="text-purple-400 animate-pulse" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Slack Interactive Messaging
            </h1>
          </div>
          <p className="text-gray-300 text-lg">
            Test Slack commands with live preview • No setup required
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm border border-green-500/30">
              ✨ Live Preview
            </span>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm border border-blue-500/30">
              🚀 Instant Testing
            </span>
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm border border-purple-500/30">
              🎯 No Slack Required
            </span>
          </div>
        </div>

        {/* Command Templates - More Visual */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {commands.map((cmd, index) => (
            <button
              key={cmd.cmd}
              onClick={() => {
                setCommand(cmd.cmd);
                setText(cmd.example);
              }}
              className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:from-white/20 hover:to-white/10 transition-all text-left overflow-hidden"
            >
              {/* Animated background effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-purple-500/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-black/30 rounded-lg">
                    {cmd.icon}
                  </div>
                  <span className="text-white font-mono text-lg">{cmd.cmd}</span>
                </div>
                <p className="text-gray-200 font-medium mb-2">{cmd.description}</p>
                <div className="bg-black/20 rounded-lg p-2 mt-3">
                  <p className="text-purple-300 text-xs font-mono">{cmd.example}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Two Column Layout: Input and Live Preview */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Command Input Panel */}
          <div className="bg-gradient-to-br from-slate-800/50 to-purple-900/30 backdrop-blur-md rounded-2xl p-8 border border-purple-500/30">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Send size={18} className="text-white" />
              </div>
              Command Builder
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-purple-300 mb-2 font-medium">Command Type</label>
                <select
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  className="w-full p-4 rounded-xl bg-black/40 text-white border border-purple-500/30 focus:border-purple-400 focus:outline-none transition-all"
                >
                  <option value="/approve">✅ /approve - Yes/No Decision</option>
                  <option value="/poll">📊 /poll - Multiple Choice</option>
                  <option value="/decide">🎯 /decide - Contextual Choice</option>
                </select>
              </div>

              <div>
                <label className="block text-purple-300 mb-2 font-medium">
                  {command === '/approve' && 'Question to approve'}
                  {command === '/poll' && 'Question? Option1 | Option2 | Option3'}
                  {command === '/decide' && 'Context | Question | OptionA | OptionB'}
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={
                    command === '/approve' ? "Should we deploy to production?" :
                    command === '/poll' ? "What's for lunch? Pizza | Tacos | Sushi" :
                    "Performance issue | Fix now or later? | Fix now | Ship first"
                  }
                  className="w-full p-4 rounded-xl bg-black/40 text-white placeholder-purple-400/50 border border-purple-500/30 focus:border-purple-400 focus:outline-none transition-all h-24 resize-none"
                />
              </div>

              <button
                onClick={simulateSlackCommand}
                disabled={loading}
                className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-purple-500/25"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent" />
                ) : (
                  <>
                    <MessageSquare size={24} />
                    Send to Slack
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Live Preview Panel */}
          <div className="bg-gradient-to-br from-slate-800/50 to-indigo-900/30 backdrop-blur-md rounded-2xl p-8 border border-indigo-500/30">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
                <MessageSquare size={18} className="text-white" />
              </div>
              Slack Preview
            </h2>

            {/* Slack Message Mock with animations */}
            <div className="bg-slate-900/80 rounded-xl p-6 border border-slate-700 relative overflow-hidden">
              {/* Animated gradient background */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 animate-gradient-x"></div>
              </div>

              <div className="relative flex items-start gap-3 mb-4">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold animate-pulse">
                    AI
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900"></div>
                </div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-white font-semibold">AI Assistant</span>
                    <span className="text-gray-500 text-xs">{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                  </div>

                  {/* Dynamic preview based on command */}
                  <div className="bg-slate-800 rounded-lg p-4 border-l-4 border-purple-500">
                    {command === '/approve' && (
                      <>
                        <p className="text-gray-300 mb-3 animate-fadeIn">{text || 'Should we deploy to production?'}</p>
                        <div className="flex gap-2">
                          <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-500/25 animate-slideInLeft">
                            ✅ Approve
                          </button>
                          <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg hover:shadow-red-500/25 animate-slideInRight">
                            ❌ Reject
                          </button>
                        </div>
                      </>
                    )}

                    {command === '/poll' && (
                      <>
                        <p className="text-gray-300 mb-3">
                          {text.split('?')[0] || "What's for lunch"}?
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {(text.split('?')[1] || 'Pizza | Tacos | Sushi').split('|').map((opt, i) => (
                            <button key={i} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                              {opt.trim()}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {command === '/decide' && (
                      <>
                        <p className="text-purple-300 text-sm mb-2">
                          Context: {text.split('|')[0] || 'Performance issue'}
                        </p>
                        <p className="text-gray-300 mb-3 font-medium">
                          {text.split('|')[1] || 'Fix now or later?'}
                        </p>
                        <div className="flex gap-2">
                          <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors">
                            ✅ {text.split('|')[2] || 'Fix now'}
                          </button>
                          <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors">
                            ❌ {text.split('|')[3] || 'Ship first'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-xs text-center mt-4">
                This is how your message will appear in Slack
              </p>
            </div>
          </div>
        </div>

        {/* Response Display */}
        {response && (
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-4">Slack Response</h2>

            {response.error ? (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                <XCircle className="text-red-400 mb-2" size={24} />
                <p className="text-red-300">{response.message}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Main message */}
                <div className="bg-black/30 rounded-lg p-4">
                  <p className="text-white font-semibold mb-2">{response.text}</p>
                </div>

                {/* Attachments (interactive elements) */}
                {response.attachments?.map((attachment: any, index: number) => (
                  <div
                    key={index}
                    className="bg-black/30 rounded-lg p-4 border-l-4"
                    style={{ borderColor: attachment.color || '#667eea' }}
                  >
                    <p className="text-white mb-4">{attachment.text}</p>

                    {/* Interactive buttons */}
                    {attachment.actions && (
                      <div className="flex gap-2 flex-wrap">
                        {attachment.actions.map((action: any) => (
                          <button
                            key={action.value}
                            onClick={() => simulateInteraction(
                              attachment.callback_id,
                              action.value
                            )}
                            className={`px-4 py-2 rounded font-semibold transition-all ${
                              action.style === 'primary'
                                ? 'bg-green-500 hover:bg-green-600 text-white'
                                : action.style === 'danger'
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : 'bg-gray-500 hover:bg-gray-600 text-white'
                            }`}
                            disabled={loading}
                          >
                            {action.text}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Fields (results) */}
                    {attachment.fields && (
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        {attachment.fields.map((field: any, i: number) => (
                          <div key={i} className={field.short ? '' : 'col-span-2'}>
                            <p className="text-gray-400 text-sm">{field.title}</p>
                            <p className="text-white">{field.value}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    {attachment.footer && (
                      <p className="text-gray-400 text-sm mt-4">{attachment.footer}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Raw JSON for debugging */}
            <details className="mt-4">
              <summary className="text-gray-400 cursor-pointer hover:text-white">
                View Raw Response
              </summary>
              <pre className="mt-2 bg-black/50 rounded p-3 text-gray-300 text-xs overflow-x-auto">
                {JSON.stringify(response, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
          <h3 className="text-xl font-semibold text-white mb-4">How to Set Up in Slack</h3>
          <ol className="space-y-2 text-gray-300">
            <li>1. Create a Slack app at api.slack.com</li>
            <li>2. Add slash commands pointing to: <code className="bg-black/30 px-2 py-1 rounded">https://futr-bet-api.b-9f2.workers.dev/slack/events</code></li>
            <li>3. Enable Interactive Components with URL: <code className="bg-black/30 px-2 py-1 rounded">https://futr-bet-api.b-9f2.workers.dev/slack/interactive</code></li>
            <li>4. Install to your workspace</li>
            <li>5. Test with <code className="bg-black/30 px-2 py-1 rounded">/approve test</code></li>
          </ol>
        </div>
      </div>
    </div>
  );
}